import { css } from "@emotion/css";
import { Callout, ExternalLink, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDUpdateAppFromTemplateProperties, ArgoCDGitCommitProperties, ArgoCDStepVerificationProperties, ArgoCDSyncProperties } from "@octopusdeploy/legacy-action-properties";
import { ArgoCDSyncModes, GitCommitMethods, StepVerificationMethods } from "@octopusdeploy/legacy-action-properties";
import { ActionExecutionLocation, GetNamedPackageReferences, GetPrimaryPackageReference, InitialisePrimaryGitDependency, InitialisePrimaryPackageReference, RemovePrimaryGitDependency, RemovePrimaryPackageReference, SetPrimaryPackageReference, } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import type { PropsWithChildren } from "react";
import { useOptionalProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { useGitCredentialsFromContext, useRefreshGitCredentialsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { useGitHubConnectionsFromContext, useRefreshGitHubConnectionsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitHubConnectionsContextProvider";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import { buildRelatedTriggerDependencies } from "~/areas/projects/components/Triggers/ProcessCallouts/triggerPackageRelations";
import { useOptionalProjectContext } from "~/areas/projects/context/index";
import { ActionInputs, isProjectOrProcessTemplateActionInput } from "~/components/ActionInputs/ActionInputs";
import type { ActionSummaryProps } from "~/components/Actions/actionSummaryProps";
import { ArgoCDApplicationsPreviewFormSection } from "~/components/Actions/argocd/ApplicationsPreview/ArgoCDApplicationsPreviewFormSection";
import { ArgoCDGitCommitMessageFormSection } from "~/components/Actions/argocd/ArgoCDGitCommitMessageFormSection";
import { ArgoCDGitCommitMethodFormSection } from "~/components/Actions/argocd/ArgoCDGitCommitMethodFormSection";
import { ArgoCDStepVerificationFormSection } from "~/components/Actions/argocd/ArgoCDStepVerificationFormSection";
import { ArgoCDTriggerSyncFormSection } from "~/components/Actions/argocd/ArgoCDTriggerSyncFormSection";
import type { ActionWithDirtyState, ActionWithFeeds, ActionWithGitRepositorySource } from "~/components/Actions/commonActionHelpers";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties } from "~/components/Actions/script/scriptPackageReference";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import { GitRepositorySourceSelector, isProjectRepositoryGitSourceSupported } from "~/components/GitRepositorySource/GitRepositorySourceSelector";
import PackageDownloadOptions from "~/components/PackageDownloadOptions/PackageDownloadOptions";
import { CardFill, type SummaryNode } from "~/components/form/Sections/ExpandableFormSection";
import ExpanderSectionHeading from "~/components/form/Sections/FormSectionHeading";
import { VariableLookupText } from "~/components/form/VariableLookupText";
import { ExpandableFormSection, Note, required, StringCheckbox, Summary } from "~/components/form/index";
import CommonSummaryHelper from "~/utils/CommonSummaryHelper/index";
class ArgoCDUpdateApplicationManifestsSummary extends BaseComponent<ActionSummaryProps, never> {
    constructor(props: ActionSummaryProps) {
        super(props);
    }
    render() {
        return <div>Update ArgoCD applications based on stored templates and Octopus variables</div>;
    }
    static displayName = "ArgoCDUpdateApplicationManifestsSummary";
}
type ArgoCDUpdateAppFromTemplateActionFields = ActionEditProps<ArgoCDUpdateAppFromTemplateProperties, ScriptPackageProperties>;
interface ArgoCDUpdateAppFromTemplateActionEditState {
    serverNames: string[];
}
export interface ArgoCDUpdateAppFromTemplateProps extends ArgoCDUpdateAppFromTemplateActionFields, ActionWithFeeds, ActionWithGitRepositorySource, ActionWithDirtyState {
}
export class ArgoCDUpdateApplicationManifestsEdit extends BaseComponent<ArgoCDUpdateAppFromTemplateProps, ArgoCDUpdateAppFromTemplateActionEditState> {
    constructor(props: ArgoCDUpdateAppFromTemplateProps) {
        super(props);
        this.state = {
            serverNames: [],
        };
    }
    private supportsProjectRepository() {
        return isProjectRepositoryGitSourceSupported(this.props.project?.PersistenceSettings, this.props.processType);
    }
    private onChangeTemplateSource(value: string) {
        this.props.setProperties({
            ["Octopus.Action.Script.ScriptSource"]: value,
        });
        if (value === "GitRepository") {
            if (this.supportsProjectRepository()) {
                this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "Project" });
                this.props.setGitDependencies?.(RemovePrimaryGitDependency(this.props.gitDependencies), false);
            }
            else {
                this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "External" });
                this.props.setGitDependencies?.(InitialisePrimaryGitDependency(this.props.gitDependencies), false);
            }
        }
        else {
            this.props.setGitDependencies?.(RemovePrimaryGitDependency(this.props.gitDependencies), false);
            this.props.setProperties({ ["Octopus.Action.GitRepository.FilePathFilters"]: undefined, ["Octopus.Action.GitRepository.Source"]: undefined });
        }
        if (value === "Package") {
            this.props.setPackages(InitialisePrimaryPackageReference(this.props.packages, this.props.feeds, isProjectOrProcessTemplateActionInput(this.props.actionInputsDependencies)));
        }
        else {
            this.props.setPackages(RemovePrimaryPackageReference(this.props.packages));
        }
    }
    packageVariableNames = (): string[] => GetNamedPackageReferences(this.props.packages).flatMap((pkg) => [
        `Octopus.Action.Package[${pkg.Name}].PackageId`,
        `Octopus.Action.Package[${pkg.Name}].FeedId`,
        `Octopus.Action.Package[${pkg.Name}].PackageVersion`,
        `Octopus.Action.Package[${pkg.Name}].Path`,
        `Octopus.Action.Package[${pkg.Name}].Image`,
    ]);
    private summaryTemplates = () => {
        const source = this.props.properties["Octopus.Action.Script.ScriptSource"];
        if (source === "GitRepository") {
            return Summary.default("File(s) from a Git repository");
        }
        if (source === "Package") {
            return Summary.summary("File(s) inside a package");
        }
        return Summary.placeholder("Resource source not specified");
    };
    getPathsNotSetValidationMessage() {
        return "Enter the paths to your template files";
    }
    getPackagePathsAccessibleName() {
        return "Paths to the template files in your Package";
    }
    getPackagePathsLabel() {
        return "Kubernetes template file paths";
    }
    private setDefaultScriptSource() {
        this.props.setProperties({ ["Octopus.Action.Script.ScriptSource"]: "GitRepository" });
        if (this.supportsProjectRepository()) {
            this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "Project" });
        }
        else {
            this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "External" });
            this.props.setGitDependencies?.(InitialisePrimaryGitDependency(this.props.gitDependencies), false);
        }
    }
    purgeOutputSummary(): SummaryNode {
        if (this.getPurgeOutputFolder()) {
            return Summary.summary("All existing app files in the Argo CD source folder will be deleted.");
        }
        else {
            return Summary.summary("Existing app files will remain in the Argo CD source folder.");
        }
    }
    getPurgeOutputFolder(): boolean {
        return this.props.properties["Octopus.Action.ArgoCD.PurgeOutputFolder"] === "True";
    }
    async componentDidMount() {
        if (!this.props.properties["Octopus.Action.Script.ScriptSource"]) {
            this.setDefaultScriptSource();
        }
        // ensure that any existing YAML paths value is copied across to the FilePathFilters so
        // the git source can be filtered properly
        if (this.props.properties["Octopus.Action.GitRepository.Source"] === "Project" && this.props.properties["Octopus.Action.ArgoCD.InputPath"] && !this.props.properties["Octopus.Action.GitRepository.FilePathFilters"]) {
            this.props.setProperties({ ["Octopus.Action.GitRepository.FilePathFilters"]: this.props.properties["Octopus.Action.ArgoCD.InputPath"] });
        }
    }
    getFilePathSummary = (filePath: string): SummaryNode => {
        if (filePath === undefined) {
            return Summary.summary("Specify the file/folder to be used as input");
        }
        else {
            return Summary.summary(<>
                    <code>{filePath}</code> will provide input templates
                </>);
        }
    };
    getPathsNote = () => <Note>Specify a single file or directory. Directories will be copied recursively.</Note>;
    packageReferenceSummary = () => {
        const namedPackageReferences = GetNamedPackageReferences(this.props.packages);
        if (namedPackageReferences.length === 0) {
            return Summary.placeholder("No additional packages referenced");
        }
        return Summary.summary(`${namedPackageReferences.length} package references`);
    };
    transformInputPathToGitFilter(inputPath: string) {
        // This does NOT support strings which start with "./" due to how globbing is done in Server
        let result = inputPath;
        if (inputPath.startsWith("./")) {
            result = result.substring(2);
        }
        if (result === "") {
            return "**/*";
        }
        else {
            const globbedResult = `${result}/**/*`.replace("//", "/");
            return `${result}\n${globbedResult}`;
        }
    }
    transformGitFilterToInputPath(gitFilter: string | undefined) {
        return this.props.properties["Octopus.Action.ArgoCD.InputPath"];
    }
    private onGitRepositoryFilePathFiltersChanged(val: string) {
        this.props.setProperties({ ["Octopus.Action.ArgoCD.InputPath"]: val });
    }
    render() {
        const pkg = GetPrimaryPackageReference(this.props.packages);
        const pkgFeed = pkg ? this.props.feeds.find((f) => f.Id === pkg.FeedId) : undefined;
        const isProjectOrProcessTemplate = isProjectOrProcessTemplateActionInput(this.props.actionInputsDependencies);
        const manifestHeadingDescription = (<div className={ManifestDescriptionStyle}>
                Provide manifest templates with Octopus variables.
                <ExternalLink href={"argo-cd-manifest-examples"}>View examples</ExternalLink>
            </div>);
        return (<>
                <ArgoCDApplicationsPreviewFormSection expandedByDefault={true} environments={this.props.environments || []}/>
                <ExpanderSectionHeading title={"Manifest Templates"} description={manifestHeadingDescription}/>
                <ExpandableFormSection errorKey="Octopus.Action.Script.ScriptSource|Octopus.Action.ArgoCD.InputPath" isExpandedByDefault={this.props.expandedByDefault} title="Template Source" fillCardWidth={CardFill.FillRight} summary={this.summaryTemplates()} help={"Select the source of the Kubernetes templates."}>
                    <RadioButtonGroup accessibleName="Template Source" value={this.props.properties["Octopus.Action.Script.ScriptSource"]} onChange={(val) => this.onChangeTemplateSource(val)} error={this.props.getFieldError("Octopus.Action.Script.ScriptSource")}>
                        <RadioButton accessibleName="Git Repository" value={"GitRepository"} label="Git Repository" isDefault={true}/>
                        <RadioButton accessibleName="Package" value={"Package"} label="Package"/>
                    </RadioButtonGroup>
                </ExpandableFormSection>
                {this.props.properties["Octopus.Action.Script.ScriptSource"] === "Package" && (<div>
                        <ExpandableFormSection errorKey="Octopus.Action.Package.FeedId|Octopus.Action.Package.PackageId" isExpandedByDefault={this.props.expandedByDefault} title="Package" summary={CommonSummaryHelper.packageSummary(pkg, this.props.feeds)} help={"Choose the package that contains the Kubernetes templates."}>
                            <ActionInputs actionUIDefinition={[{ type: "deferred-package-selector", key: "deferred-package-selector", hasModelDirty: true, hasRelatedTriggersDependencies: true }]} dependencies={this.props.actionInputsDependencies}/>
                            <PackageDownloadOptions packageAcquisitionLocation={pkg.AcquisitionLocation} onPackageAcquisitionLocationChanged={(acquisitionLocation) => this.props.setPackages(SetPrimaryPackageReference({ AcquisitionLocation: acquisitionLocation }, this.props.packages))} feed={pkgFeed} projectId={this.props.projectId} localNames={this.props.localNames}/>
                        </ExpandableFormSection>
                    </div>)}
                {this.props.properties["Octopus.Action.Script.ScriptSource"] === "GitRepository" && (<GitRepositorySourceSelector properties={this.props.properties} gitCredentials={this.props.gitCredentials} refreshGitCredentials={this.props.refreshGitCredentials} gitHubConnections={this.props.gitHubConnections} refreshGitHubConnections={this.props.refreshGitHubConnections} project={this.props.project} processType={this.props.processType} gitDependencies={this.props.gitDependencies} expandedByDefault={this.props.expandedByDefault} getFieldError={this.props.getFieldError} setProperties={this.props.setProperties} setGitDependencies={this.props.setGitDependencies} localNames={this.props.localNames} enableFilePathFilters={true} onFilePathFilterChanged={(filePaths) => this.onGitRepositoryFilePathFiltersChanged(filePaths)} filePathOverrides={{
                    accessibleName: "Path to the Template files in your Git Repository",
                    label: this.getPackagePathsLabel(),
                    help: "Provide the path to your template file or directory.",
                    customPropertyName: "Octopus.Action.ArgoCD.InputPath",
                    validation: validateFilePaths,
                    summary: (filePath) => (filePath ? this.getFilePathSummary(filePath) : Summary.placeholder("Provide the path to your template file or directory.")),
                    multiline: false,
                    title: "Path",
                    notes: this.getPathsNote,
                    transforms: {
                        filePathToGitFilter: (filePath) => this.transformInputPathToGitFilter(filePath),
                        gitFilterToFilePath: (gitFilter) => this.transformGitFilterToInputPath(gitFilter),
                    },
                }} relatedTriggerDependencies={buildRelatedTriggerDependencies(this.props.inputDependencies)} hideGitConnectionsAndAddRefresh={isProjectOrProcessTemplate}/>)}
                {this.props.properties["Octopus.Action.Script.ScriptSource"] === "Package" && (<ExpandableFormSection errorKey="Path" title={"Input Path"} summary={this.getFilePathSummary(this.props.properties["Octopus.Action.ArgoCD.InputPath"])} help={"Provide the path to your input files or directory."} isExpandedByDefault={this.props.expandedByDefault}>
                        <VariableLookupText localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.InputPath"]} onChange={(filePaths) => this.onGitRepositoryFilePathFiltersChanged(filePaths)} label={this.getPackagePathsLabel()} error={this.props.getFieldError("Octopus.Action.ArgoCD.InputPath")} validate={(p) => validateFilePaths(p, this.getPathsNotSetValidationMessage())} accessibleName={this.getPackagePathsAccessibleName()} multiline={false}/>
                        {this.getPathsNote()}
                    </ExpandableFormSection>)}
                <ExpanderSectionHeading title="Git Commit Settings" description="Octopus will commit your updated manifests to the Argo CD source folder in Git."/>
                <ArgoCDGitCommitMessageFormSection messageSummary={this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"]} updateMessageSummary={(summary) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageSummary"]: summary })} messageSummaryError={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMessageSummary")} messageDescription={this.props.properties["Octopus.Action.ArgoCD.CommitMessageDescription"]} updateMessageDescription={(description) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageDescription"]: description })} messageDescriptionError={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMessageDescription")} localNames={this.props.localNames} expandedByDefault={this.props.expandedByDefault} messageDescriptionHelp={"Octopus will use only the summary if no description is supplied."}/>
                <ArgoCDGitCommitMethodFormSection expandedByDefault={this.props.expandedByDefault} environments={this.props.environments} properties={this.props.properties} setProperties={this.props.setProperties} processType={this.props.processType} persistenceSettings={this.props.project?.PersistenceSettings} onParameterAdded={isProjectOrProcessTemplateActionInput(this.props.actionInputsDependencies) ? this.props.actionInputsDependencies.onParameterAdded : undefined}/>
                <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.PurgeOutputFolder" title="Purge Argo CD Source Folder" summary={this.purgeOutputSummary()} help="Delete all manifest files in the output folder that weren’t created during the current deployment." isExpandedByDefault={this.props.expandedByDefault}>
                    <StringCheckbox value={this.props.properties["Octopus.Action.ArgoCD.PurgeOutputFolder"]} onChange={(val) => this.props.setProperties({ ["Octopus.Action.ArgoCD.PurgeOutputFolder"]: val })} label={"Enable output folder purging"}/>
                    {this.getPurgeOutputFolder() && (<Callout type={"warning"} hideTitle={true}>
                            Purging permanently deletes all existing files the output folder.
                        </Callout>)}
                </ExpandableFormSection>
                <ExpanderSectionHeading title="Additional Configuration Options"/>
                {isFeatureToggleEnabled("verify-argocd-pull-requests-merged") || isFeatureToggleEnabled("verify-argocd-application-health") ? (<ArgoCDStepVerificationFormSection expandedByDefault={this.props.expandedByDefault} properties={this.props.properties} setProperties={this.props.setProperties} getFieldError={this.props.getFieldError}/>) : null}
                <FeatureToggleVisibility toggle={"argocd-sync"}>
                    <ArgoCDTriggerSyncFormSection expandedByDefault={this.props.expandedByDefault} environments={this.props.environments} properties={this.props.properties} setProperties={this.props.setProperties} processType={this.props.processType} persistenceSettings={this.props.project?.PersistenceSettings} onParameterAdded={isProjectOrProcessTemplateActionInput(this.props.actionInputsDependencies) ? this.props.actionInputsDependencies.onParameterAdded : undefined}/>
                </FeatureToggleVisibility>
                <ExpandableFormSection title={"Container Images"} isExpandedByDefault={this.props.expandedByDefault} errorKey="Octopus.Action.Script.Packages" summary={this.packageReferenceSummary()} help={"Add the container image(s) whose update can be used as a trigger for this project."}>
                    <Note>
                        Learn more about <ExternalLink href="ScriptStepPackageReferences">package/container references</ExternalLink>.
                    </Note>

                    <ActionInputs actionUIDefinition={[{ type: "container-image-reference-list", key: "container-image-reference-list", hideAcquisitionChoices: true, hasRelatedTriggersDependencies: true }]} dependencies={this.props.actionInputsDependencies}/>
                </ExpandableFormSection>
            </>);
    }
    static displayName = "ArgoCDUpdateApplicationManifestsEdit";
}
export function validateFilePaths(pathString: string, notSetValidationMessage: string) {
    const requiredMessage = required(notSetValidationMessage)(pathString);
    if (requiredMessage || pathString.trim().length < 1) {
        return notSetValidationMessage;
    }
    // This Regex matches:
    //  - Paths which start with a root directory eg: '/', '\', 'C:\'
    //  - Paths which use either '..' directories eg: 'folder/../', '../' - to prevent moving back below the package root.
    //  - will not support globs (i.e.*)
    const invalidPathRegexes = ["^[A-Za-z]:(\\\\|/|$)", "(^|(^|\\\\|/)[.][.])(\\\\|/|$)", "\\*"];
    if (invalidPathRegexes.some((r) => pathString.trim().match(r))) {
        return "The path to your YAML file(s) cannot stem from a root/absolute directory, use '..' nor contain glob patterns.";
    }
    return "";
}
export function ArgoCDUpdateApplicationManifests(props: PropsWithChildren<ArgoCDUpdateAppFromTemplateActionFields>) {
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const gitCredentials = useGitCredentialsFromContext();
    const refreshGitCredentials = useRefreshGitCredentialsFromContext();
    const gitHubConnections = useGitHubConnectionsFromContext();
    const refreshGitHubConnections = useRefreshGitHubConnectionsFromContext();
    const projectContext = useOptionalProjectContext();
    const processContext = useOptionalProcessContext();
    return (<ArgoCDUpdateApplicationManifestsEdit {...props} feeds={feeds} refreshFeeds={refreshFeeds} project={projectContext?.state.model} processType={processContext?.selectors.getProcessType()} gitCredentials={gitCredentials} refreshGitCredentials={refreshGitCredentials} gitHubConnections={gitHubConnections} refreshGitHubConnections={refreshGitHubConnections} modelDirty={!isEqual(processContext?.state.model, processContext?.state.cleanModel)}/>);
}
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    canRunOnWorker: true,
    canRunInContainer: true,
    disableInlineExecutionContainers: true,
    actionType: "Octopus.ArgoCDUpdateManifests",
    summary: (properties, targetRolesAsCSV) => <ArgoCDUpdateApplicationManifestsSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    editSections: {
        top: (props: ArgoCDUpdateAppFromTemplateActionFields) => (<>
                <ArgoCDUpdateApplicationManifests {...props}/>
                <ExpanderSectionHeading title="Step Execution Location"/>
            </>),
        default: (_: ArgoCDUpdateAppFromTemplateActionFields) => <></>,
    },
    getInitialProperties: () => ({
        ...InitialCommitProperties,
        ...InitialStepVerificationMethod,
        ...InitialSyncMode,
    }),
    canHaveChildren: (step) => false,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.None,
    docsLink: "updateArgoCDAppFromTemplate",
    canUseExecutionTimeouts: false,
});
const defaultCommitMessageSummary = "Updated Manifests with Release: #{Octopus.Release.Number}";
//Update ArgoCDDefaultCommitMessageBodyTests on the server side to test the expression
const defaultCommitMessageBody = "Project: #{Octopus.Project.Slug}\nEnvironment: #{Octopus.Environment.Slug}#{if Octopus.Deployment.Tenant.Slug }\nTenant: #{Octopus.Deployment.Tenant.Slug}#{/if}";
const InitialCommitProperties: Partial<ArgoCDGitCommitProperties> = {
    "Octopus.Action.ArgoCD.CommitMessageSummary": defaultCommitMessageSummary,
    "Octopus.Action.ArgoCD.CommitMessageDescription": defaultCommitMessageBody,
    "Octopus.Action.ArgoCD.CommitMethod": GitCommitMethods.DirectCommit,
};
const InitialStepVerificationMethod: ArgoCDStepVerificationProperties = {
    "Octopus.Action.ArgoCD.StepVerification.Method": StepVerificationMethods.ArgoCDApplicationHealthy,
    "Octopus.Action.ArgoCD.StepVerification.Timeout": null,
};
const InitialSyncMode: Partial<ArgoCDSyncProperties> = {
    "Octopus.Action.ArgoCD.Sync.Mode": ArgoCDSyncModes.AllEnvironments,
};
const ManifestDescriptionStyle = css({
    display: "flex",
    flowDirection: "row",
    gap: space[6],
});
